<template>
  <div>
    <!-- MODO TELA NORMAL: seleção de conta e período -->
    <div v-if="!printMode">
      <h6>Conciliação Bancária</h6>
      <div class="row mb-3">
        <div class="col-md-4">
          <label>Conta Bancária:</label>
          <select v-model="selectedContaId" class="form-control">
            <option value="" disabled>Selecione a conta</option>
            <option v-for="conta in contas" :key="conta.id" :value="conta.id">
              {{ conta.apelido }} - {{ conta.agencia }} - {{ conta.numero_conta }}
            </option>
          </select>
        </div>
        <div class="col-auto">
          <label>Data Início:</label>
          <input type="date" v-model="dataInicioLocal" class="form-control" />
        </div>
        <div class="col-auto">
          <label>Data Fim:</label>
          <input type="date" v-model="dataFimLocal" class="form-control" />
        </div>
        <div class="col-auto align-self-end">
          <button @click="abrirPrint" class="btn btn-primary" :disabled="!podeExecutar">
            Executar
          </button>
        </div>
      </div>
    </div>

    <!-- MODO PRINT: saldo, receitas e despesas -->
    <div v-else>
      <!-- Título dinâmico com conta -->
      
      <!-- Subtítulo do período -->
      <h6 class="text-center mb-3" style="font-size: 14px;">
        {{ props.subtitle }}
      </h6>

      <div v-if="loading">Carregando…</div>
      <div v-else-if="error" role="alert" class="alert alert-danger">
        {{ error }} <button class="btn btn-sm btn-outline-danger no-print" @click="fetchData">Tentar novamente</button>
      </div>
      <template v-else-if="loaded">
      <!-- Tabela de Saldos -->
      <div class="mb-4">
        <table class="table table-bordered text-center">
          <thead>
            <tr>
              <th>Saldo Anterior</th>
              <th>Saldo Final do Período</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{{ formatarMoeda(saldoAnterior) }}</td>
              <td>{{ formatarMoeda(saldoAtual) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="referencias.length">
        <h6>Diferenças de referência (não são receitas ou despesas)</h6>
        <table class="table table-bordered">
          <thead><tr><th>Data</th><th>Referência</th><th>Calculado antes</th><th>Declarado</th><th>Diferença</th></tr></thead>
          <tbody><tr v-for="row in referencias" :key="row[1]">
            <td>{{ row[0] }}</td><td>{{ row[1] }}</td><td>{{ formatarMoeda(row[2]) }}</td>
            <td>{{ formatarMoeda(row[3]) }}</td><td>{{ formatarMoeda(row[4]) }}</td>
          </tr></tbody>
        </table>
      </div>
      <div>
        <!-- Receitas -->
        <div v-if="receitas.length">
          <h6>Receitas</h6>
          <table class="table table-striped mb-4">
            <thead>
              <tr>
                <th>Prev. Receb.</th>
                <th class="text-right">Valor</th>
                <th>Refil – Plano de Contas</th>
                <th>Descrição</th>
                <th class="text-center">Forma de Pagamento</th>
                <th class="text-center">Autorização</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in receitas" :key="`rec-${idx}`">
                <td>{{ globalUtils.formatarData(item.recebimento) }}</td>
                <td class="text-right">{{ formatarMoeda(item.valor) }}</td>
                <td>{{ item.refil }}</td>
                <td>{{ item.descricao }}</td>
                <td class="text-center">{{ item.forma_pagamento }}</td>
                <td class="text-center">{{ item.numero_autorizacao || '—' }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total Receitas</strong></td>
                <td class="text-right"><strong>{{ formatarMoeda(totalReceitas) }}</strong></td>
                <td colspan="3"></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else>
          <p class="text-muted">Nenhuma receita encontrada para este período.</p>
        </div>

        <!-- Despesas -->
        <div v-if="despesas.length">
          <h6>Despesas</h6>
          <table class="table table-striped">
            <thead>
              <tr>
                <th>Pagamento</th>
                <th class="text-right">Valor</th>
                <th>Refil – Plano de Contas</th>
                <th>Descrição</th>
                <th>Forma de Pagamento</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in despesas" :key="`desp-${idx}`">
                <td>{{ globalUtils.formatarData(item.pagamento) }}</td>
                <td class="text-right">{{ formatarMoeda(item.valor) }}</td>
                <td>{{ item.refil }}</td>
                <td>{{ item.descricao }}</td>
                <td>{{ item.forma_pagamento }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="2"><strong>Total Despesas</strong></td>
                <td class="text-right"><strong>{{ formatarMoeda(totalDespesas) }}</strong></td>
                <td colspan="4"></td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-else>
          <p class="text-muted">Nenhuma despesa encontrada para este período.</p>
        </div>
      </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { linhasReferencias } from '@/utils/extratoPresentation.mjs'
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import globalUtils from '@/utils/globalUtils.js'

// Props vindas do ReportPrintWrapper
const props = defineProps({
  printMode:  { type: Boolean, default: false },
  printTitle: { type: String, default: 'Relatório' },
  contaId:    String,
  dataInicio: String,
  dataFim:    String,
  subtitle:   { type: String, default: '' }
})

const router = useRouter()

// Estados para tela normal
const contas = ref([])
const selectedContaId = ref('')
const dataInicioLocal = ref('')
const dataFimLocal = ref('')

// Estados para print mode
const loading = ref(false)
const error = ref(''), loaded = ref(false)
const referencias = ref([])
const saldoAnterior = ref(null)
const saldoAtual = ref(null)
const receitas = ref([])
const despesas = ref([])
const totalReceitas = ref(0)
const totalDespesas = ref(0)

// Habilita botão Executar
const podeExecutar = computed(() =>
  selectedContaId.value && dataInicioLocal.value && dataFimLocal.value
)

// Busca lista de contas
async function fetchContas() {
  try {
    const res = await axios.get('/gestao/relatorios/financeiros/conciliacao-bancaria/contas', {
      params: { page: 1, limit: 1000 }
    })
    const lista = Array.isArray(res.data.data) ? res.data.data : []
    contas.value = lista.map(c => ({
      id: c.id,
      apelido: c.apelido,
      instituicao: c.instituicao,
      agencia: c.agencia,
      numero_conta: c.numero_conta
    }))
  } catch (e) {
    console.error('Erro ao buscar contas:', e)
  }
}

// Função Executar -> print
function abrirPrint() {
  if (!podeExecutar.value) return
  const conta = contas.value.find(c => c.id === selectedContaId.value)
  const contaLabel = conta
    ? `${conta.apelido} - ${conta.agencia} - ${conta.numero_conta}`
    : ''
  const tituloLocal = contaLabel
    ? `Conciliação Bancária (${contaLabel})`
    : 'Conciliação Bancária'

  const inicioFmt = globalUtils.formatarData(dataInicioLocal.value)
  const fimFmt    = globalUtils.formatarData(dataFimLocal.value)
  const subtitleLocal = `( ${inicioFmt} a ${fimFmt} )`

  router.push({
    path: '/gestao/relatorios/print/conciliacao-bancaria',
    query: {
      title:      tituloLocal,
      contaId:    selectedContaId.value,
      dataInicio: dataInicioLocal.value,
      dataFim:    dataFimLocal.value,
      subtitle:   subtitleLocal
    }
  })
}

// Busca dados no print mode
async function fetchData() {
  loading.value = true
  error.value = ''; loaded.value = false
  receitas.value = []; despesas.value = []; referencias.value = []
  saldoAnterior.value = null; saldoAtual.value = null
  try {
    const res = await axios.get(
      '/gestao/relatorios/financeiros/conciliacao-bancaria',
      { params: {
          contaId: props.contaId,
          dataInicio: props.dataInicio,
          dataFim: props.dataFim
      }}
    )
    const data = res.data
    referencias.value = linhasReferencias(data.referencias_periodo)
    saldoAnterior.value = data.saldoAnterior
    saldoAtual.value    = data.saldoAtual
    receitas.value      = Array.isArray(data.receitas) ? data.receitas : []
    receitas.value.sort((a, b) => new Date(a.recebimento) - new Date(b.recebimento))
    despesas.value      = Array.isArray(data.despesas) ? data.despesas : []
    totalReceitas.value = parseFloat(data.totalReceitas) || 0
    totalDespesas.value = parseFloat(data.totalDespesas) || 0
    loaded.value = true
  } catch (e) {
    error.value = e?.response?.data?.message || 'Falha ao carregar o relatório. Tente novamente.'
  } finally {
    loading.value = false
  }
}

// Montagem inicial
onMounted(() => {
  if (!props.printMode) fetchContas()
  else fetchData()
})

// Formata moeda
function formatarMoeda(num) {
  if (num == null) return 'Desconhecido'
  return Number(num || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  })
}
</script>

<style scoped>
.mb-3 { margin-bottom: 1rem; }
.text-right { text-align: right !important; }
</style>
